// NOTE: NSAI-7215 — validation for the Intelligent UI contract (```ui).
//
// Two layers, like the diagram and map contracts. The zod shapes enforce the
// budgets and field types; the cross-checks in `addCrossReferenceIssues` then
// enforce what a shape cannot: every formula parses, every name a formula reads
// exists, derived values do not depend on each other in a circle, a comparison
// has one cell per option, a table row has one cell per column.
//
// Every message names the field and the way out, because the model is the one
// that has to fix the spec.

import { z } from "zod";

import {
  MAX_UI_ACTION_PROMPT_LENGTH,
  MAX_UI_ACTIONS,
  MAX_UI_BLOCKS,
  MAX_UI_CELL_LENGTH,
  MAX_UI_COMPARISON_ATTRIBUTES,
  MAX_UI_COMPARISON_OPTIONS,
  MAX_UI_EXPRESSION_LENGTH,
  MAX_UI_FULL_TEXT_LENGTH,
  MAX_UI_ID_LENGTH,
  MAX_UI_INPUTS,
  MAX_UI_LABEL_LENGTH,
  MAX_UI_PLOT_SERIES,
  MAX_UI_SELECT_OPTIONS,
  MAX_UI_STATS_ITEMS,
  MAX_UI_STEPS,
  MAX_UI_TAB_BLOCKS,
  MAX_UI_TABLE_COLUMNS,
  MAX_UI_TABLE_ROWS,
  MAX_UI_TABS,
  MAX_UI_TITLE_LENGTH,
  MAX_UI_VALUES,
  MIN_UI_COMPARISON_OPTIONS,
  MIN_UI_TABS,
  UI_ACTION_PLACEHOLDER_PATTERN,
  UI_ID_PATTERN,
  UI_INVALID_JSON_RESULT,
  UI_PLOT_VARIABLE,
  UI_RESERVED_IDENTIFIERS,
} from "../constants/ui";
import { reshapeUiComparisons } from "../lib/ui/comparison-shape";
import {
  parseUiExpression,
  UI_EXPRESSION_FUNCTION_NAMES,
} from "../lib/ui/expression";
import { repairUiJson } from "../lib/ui/json-repair";
import { readUiNumeral } from "../lib/ui/leniency";
import { orderUiValues } from "../lib/ui/scope";
import {
  EUiBlockType,
  EUiInputKind,
  EUiParseFailure,
  EUiPlotKind,
  EUiTableAlign,
  type IRepairUiJsonProps,
  type IUiSpec,
  type TParseUiResult,
  type TUiBlock,
  type TUiInput,
} from "../types/ui";
import { chartDataSchema, chartValueFormatSchema } from "./chart";
import { diagramSpecSchema } from "./diagram";
import { mapSpecSchema } from "./map";

const FUNCTION_NAMES: ReadonlySet<string> = new Set(
  UI_EXPRESSION_FUNCTION_NAMES,
);

const titleSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_UI_TITLE_LENGTH, {
    message: `A title is at most ${MAX_UI_TITLE_LENGTH} characters — shorten it`,
  });

const labelSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_UI_LABEL_LENGTH, {
    message: `A label is at most ${MAX_UI_LABEL_LENGTH} characters — shorten the wording; detail belongs in the prose`,
  });

// NOTE: NSAI-7841 — free text past `MAX_UI_TEXT_LENGTH` is kept whole rather
// than refused, and cut only on screen (`readUiDisplayText`), where an info
// icon shows the rest; see lib/ui/leniency.ts for why free text is the one
// place that is safe.
const textSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_UI_FULL_TEXT_LENGTH, {
    message: `This text is at most ${MAX_UI_FULL_TEXT_LENGTH} characters — put longer explanations in the prose around the block`,
  });

const expressionSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_UI_EXPRESSION_LENGTH, {
    message: `A formula is at most ${MAX_UI_EXPRESSION_LENGTH} characters — move part of it into a named value`,
  });

const idSchema = z
  .string()
  .max(MAX_UI_ID_LENGTH)
  .regex(UI_ID_PATTERN, {
    message:
      "An id is letters, digits and underscores, starting with a letter — it is used inside formulas",
  })
  .refine((id) => !UI_RESERVED_IDENTIFIERS.has(id) && !FUNCTION_NAMES.has(id), {
    message: `This id is reserved (true, false, ${UI_PLOT_VARIABLE} and function names) — pick another`,
  });

const finiteNumberSchema = z.number().finite();

// NOTE: NSAI-7841 — a field that only ever holds a number also takes one
// written as text ("1"). Table cells keep `finiteNumberSchema`: there "10" is
// legitimately text, and converting it would change how the cell reads.
const numberFieldSchema = z.preprocess(readUiNumeral, finiteNumberSchema);

const positiveNumberFieldSchema = z.preprocess(
  readUiNumeral,
  finiteNumberSchema.positive(),
);

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

const sliderInputSchema = z
  .object({
    id: idSchema,
    kind: z.literal(EUiInputKind.SLIDER),
    label: labelSchema,
    min: numberFieldSchema,
    max: numberFieldSchema,
    value: numberFieldSchema,
    step: positiveNumberFieldSchema.optional(),
    unit: z.string().trim().min(1).max(12).optional(),
  })
  .strict();

const numberInputSchema = z
  .object({
    id: idSchema,
    kind: z.literal(EUiInputKind.NUMBER),
    label: labelSchema,
    value: numberFieldSchema,
    min: numberFieldSchema.optional(),
    max: numberFieldSchema.optional(),
    step: positiveNumberFieldSchema.optional(),
    unit: z.string().trim().min(1).max(12).optional(),
  })
  .strict();

const selectInputSchema = z
  .object({
    id: idSchema,
    kind: z.literal(EUiInputKind.SELECT),
    label: labelSchema,
    options: z
      .array(
        z.object({ label: labelSchema, value: numberFieldSchema }).strict(),
      )
      .min(2, { message: "A select needs at least 2 options" })
      .max(MAX_UI_SELECT_OPTIONS, {
        message: `A select offers at most ${MAX_UI_SELECT_OPTIONS} options`,
      }),
    value: numberFieldSchema,
  })
  .strict();

const toggleInputSchema = z
  .object({
    id: idSchema,
    kind: z.literal(EUiInputKind.TOGGLE),
    label: labelSchema,
    value: z.boolean(),
  })
  .strict();

const inputSchema = z.discriminatedUnion("kind", [
  sliderInputSchema,
  numberInputSchema,
  selectInputSchema,
  toggleInputSchema,
]);

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

const statsBlockSchema = z
  .object({
    type: z.literal(EUiBlockType.STATS),
    title: titleSchema.optional(),
    items: z
      .array(
        z
          .object({
            label: labelSchema,
            value: expressionSchema,
            format: chartValueFormatSchema.optional(),
            hint: textSchema.optional(),
          })
          .strict(),
      )
      .min(1)
      .max(MAX_UI_STATS_ITEMS, {
        message: `stats shows at most ${MAX_UI_STATS_ITEMS} tiles — pick the numbers that answer the question`,
      }),
  })
  .strict();

const comparisonBlockSchema = z
  .object({
    type: z.literal(EUiBlockType.COMPARISON),
    title: titleSchema.optional(),
    options: z
      .array(
        z
          .object({
            id: idSchema,
            label: labelSchema,
            sublabel: labelSchema.optional(),
          })
          .strict(),
      )
      .min(MIN_UI_COMPARISON_OPTIONS, {
        message: `A comparison needs at least ${MIN_UI_COMPARISON_OPTIONS} options`,
      })
      .max(MAX_UI_COMPARISON_OPTIONS, {
        message: `A comparison shows at most ${MAX_UI_COMPARISON_OPTIONS} options side by side — use a table for more`,
      }),
    attributes: z
      .array(
        z
          .object({
            label: labelSchema,
            values: z.array(
              z
                .string()
                .trim()
                .min(1)
                .max(MAX_UI_CELL_LENGTH, {
                  message: `A comparison cell is at most ${MAX_UI_CELL_LENGTH} characters`,
                }),
            ),
          })
          .strict(),
      )
      .min(1)
      .max(MAX_UI_COMPARISON_ATTRIBUTES, {
        message: `A comparison has at most ${MAX_UI_COMPARISON_ATTRIBUTES} rows — keep the attributes that decide the choice`,
      }),
    recommended: z.string().optional(),
    verdict: textSchema.optional(),
  })
  .strict();

const tableCellSchema = z.union([
  z
    .string()
    .trim()
    .max(MAX_UI_CELL_LENGTH, {
      message: `A table cell is at most ${MAX_UI_CELL_LENGTH} characters`,
    }),
  finiteNumberSchema,
]);

const tableBlockSchema = z
  .object({
    type: z.literal(EUiBlockType.TABLE),
    title: titleSchema.optional(),
    columns: z
      .array(
        z
          .object({
            label: labelSchema,
            align: z.nativeEnum(EUiTableAlign).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(MAX_UI_TABLE_COLUMNS, {
        message: `A table has at most ${MAX_UI_TABLE_COLUMNS} columns`,
      }),
    rows: z
      .array(z.array(tableCellSchema))
      .min(1)
      .max(MAX_UI_TABLE_ROWS, {
        message: `A table has at most ${MAX_UI_TABLE_ROWS} rows — summarise or split it`,
      }),
  })
  .strict();

const stepsBlockSchema = z
  .object({
    type: z.literal(EUiBlockType.STEPS),
    title: titleSchema.optional(),
    items: z
      .array(
        z.object({ label: textSchema, detail: textSchema.optional() }).strict(),
      )
      .min(1)
      .max(MAX_UI_STEPS, {
        message: `steps lists at most ${MAX_UI_STEPS} items — group them into phases`,
      }),
  })
  .strict();

const actionsBlockSchema = z
  .object({
    type: z.literal(EUiBlockType.ACTIONS),
    items: z
      .array(
        z
          .object({
            label: labelSchema,
            prompt: z
              .string()
              .trim()
              .min(1)
              .max(MAX_UI_ACTION_PROMPT_LENGTH, {
                message: `An action prompt is at most ${MAX_UI_ACTION_PROMPT_LENGTH} characters`,
              }),
          })
          .strict(),
      )
      .min(1)
      .max(MAX_UI_ACTIONS, {
        message: `Offer at most ${MAX_UI_ACTIONS} next steps — only the obvious ones`,
      }),
  })
  .strict();

const plotBlockSchema = z
  .object({
    type: z.literal(EUiBlockType.PLOT),
    kind: z.nativeEnum(EUiPlotKind),
    from: expressionSchema,
    to: expressionSchema,
    step: positiveNumberFieldSchema.optional(),
    series: z
      .array(z.object({ label: labelSchema, y: expressionSchema }).strict())
      .min(1)
      .max(MAX_UI_PLOT_SERIES, {
        message: `A plot draws at most ${MAX_UI_PLOT_SERIES} series`,
      })
      // NOTE: The labels become chart series names, which the chart contract
      // requires to be unique regardless of case.
      .superRefine((series, context) => {
        const seen = new Set<string>();

        series.forEach((seriesSpec, seriesIndex) => {
          const key = seriesSpec.label.toLowerCase();

          if (seen.has(key)) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              path: [seriesIndex, "label"],
              message: `"${seriesSpec.label}" is already a series label in this plot — give each series its own name`,
            });
          }

          seen.add(key);
        });
      }),
    title: titleSchema.optional(),
    subtitle: textSchema.optional(),
    xLabel: labelSchema.optional(),
    format: chartValueFormatSchema.optional(),
  })
  .strict();

const chartBlockSchema = z
  .object({ type: z.literal(EUiBlockType.CHART), spec: chartDataSchema })
  .strict();

const diagramBlockSchema = z
  .object({ type: z.literal(EUiBlockType.DIAGRAM), spec: diagramSpecSchema })
  .strict();

const mapBlockSchema = z
  .object({ type: z.literal(EUiBlockType.MAP), spec: mapSpecSchema })
  .strict();

const leafBlockSchema = z.discriminatedUnion("type", [
  statsBlockSchema,
  comparisonBlockSchema,
  tableBlockSchema,
  stepsBlockSchema,
  actionsBlockSchema,
  plotBlockSchema,
  chartBlockSchema,
  diagramBlockSchema,
  mapBlockSchema,
]);

const tabsBlockSchema = z
  .object({
    type: z.literal(EUiBlockType.TABS),
    tabs: z
      .array(
        z
          .object({
            label: labelSchema,
            blocks: z
              .array(leafBlockSchema)
              .min(1)
              .max(MAX_UI_TAB_BLOCKS, {
                message: `A tab holds at most ${MAX_UI_TAB_BLOCKS} blocks`,
              }),
          })
          .strict(),
      )
      .min(MIN_UI_TABS, { message: `tabs needs at least ${MIN_UI_TABS} tabs` })
      .max(MAX_UI_TABS, { message: `tabs shows at most ${MAX_UI_TABS} tabs` }),
  })
  .strict();

// NOTE: NSAI-7841 — discriminated on `type` like `leafBlockSchema`, not a plain
// union: when every branch of a plain union fails, zod reports only "blocks.N:
// Invalid input", which names neither the field nor the way out.
const blockSchema = z.discriminatedUnion("type", [
  ...leafBlockSchema.options,
  tabsBlockSchema,
]);

// ---------------------------------------------------------------------------
// Cross-references
// ---------------------------------------------------------------------------

interface ICheckFormulaProps {
  expression: string;
  knownIds: ReadonlySet<string>;
  path: (string | number)[];
  context: z.RefinementCtx;
  allowPlotVariable?: boolean;
}

const checkFormula = ({
  expression,
  knownIds,
  path,
  context,
  allowPlotVariable,
}: ICheckFormulaProps): void => {
  const parsed = parseUiExpression(expression);

  if (!parsed.success) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: parsed.error,
      path,
    });
    return;
  }

  const unknown = parsed.identifiers.find(
    (name) =>
      !knownIds.has(name) &&
      !(allowPlotVariable === true && name === UI_PLOT_VARIABLE),
  );

  if (unknown !== undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: `"${unknown}" is not an input or a value — define it in inputs or values, or fix the spelling`,
      path,
    });
  }
};

interface ICheckInputProps {
  input: TUiInput;
  path: (string | number)[];
  context: z.RefinementCtx;
}

const checkInput = ({ input, path, context }: ICheckInputProps): void => {
  const addIssue = (message: string): void =>
    context.addIssue({ code: z.ZodIssueCode.custom, message, path });

  if (input.kind === EUiInputKind.SLIDER) {
    if (input.min >= input.max) {
      addIssue("A slider's min must be below its max");
    } else if (input.value < input.min || input.value > input.max) {
      addIssue("A slider's value must lie between its min and max");
    }
  }

  if (input.kind === EUiInputKind.NUMBER) {
    if (input.min !== undefined && input.value < input.min) {
      addIssue("A number input's value is below its min");
    }

    if (input.max !== undefined && input.value > input.max) {
      addIssue("A number input's value is above its max");
    }
  }

  if (
    input.kind === EUiInputKind.SELECT &&
    !input.options.some((option) => option.value === input.value)
  ) {
    addIssue("A select's value must be the value of one of its options");
  }
};

interface ICheckBlockProps {
  block: TUiBlock;
  knownIds: ReadonlySet<string>;
  path: (string | number)[];
  context: z.RefinementCtx;
}

const checkBlock = ({
  block,
  knownIds,
  path,
  context,
}: ICheckBlockProps): void => {
  switch (block.type) {
    case EUiBlockType.STATS:
      block.items.forEach((item, itemIndex) =>
        checkFormula({
          expression: item.value,
          knownIds,
          path: [...path, "items", itemIndex, "value"],
          context,
        }),
      );
      return;
    case EUiBlockType.PLOT:
      checkFormula({
        expression: block.from,
        knownIds,
        path: [...path, "from"],
        context,
      });
      checkFormula({
        expression: block.to,
        knownIds,
        path: [...path, "to"],
        context,
      });
      block.series.forEach((series, seriesIndex) =>
        checkFormula({
          expression: series.y,
          knownIds,
          path: [...path, "series", seriesIndex, "y"],
          context,
          allowPlotVariable: true,
        }),
      );
      return;
    case EUiBlockType.COMPARISON: {
      const optionIds = block.options.map((option) => option.id);

      if (new Set(optionIds).size !== optionIds.length) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Every option id must be unique",
          path: [...path, "options"],
        });
      }

      block.attributes.forEach((attribute, attributeIndex) => {
        if (attribute.values.length !== block.options.length) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Each attribute needs exactly one value per option (${block.options.length}), in the order the options are listed`,
            path: [...path, "attributes", attributeIndex, "values"],
          });
        }
      });

      if (
        block.recommended !== undefined &&
        !optionIds.includes(block.recommended)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "recommended must be the id of one of the options",
          path: [...path, "recommended"],
        });
      }
      return;
    }
    case EUiBlockType.TABLE:
      block.rows.forEach((row, rowIndex) => {
        if (row.length !== block.columns.length) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Each row needs exactly one cell per column (${block.columns.length})`,
            path: [...path, "rows", rowIndex],
          });
        }
      });
      return;
    case EUiBlockType.ACTIONS:
      block.items.forEach((item, itemIndex) => {
        for (const match of item.prompt.matchAll(
          UI_ACTION_PLACEHOLDER_PATTERN,
        )) {
          if (!knownIds.has(match[1])) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: `{${match[1]}} is not an input or a value — placeholders name an id from inputs or values`,
              path: [...path, "items", itemIndex, "prompt"],
            });
          }
        }
      });
      return;
    case EUiBlockType.TABS:
      block.tabs.forEach((tab, tabIndex) =>
        tab.blocks.forEach((child, childIndex) =>
          checkBlock({
            block: child,
            knownIds,
            path: [...path, "tabs", tabIndex, "blocks", childIndex],
            context,
          }),
        ),
      );
      return;
    default:
      return;
  }
};

interface IAddCrossReferenceIssuesProps {
  spec: IUiSpec;
  context: z.RefinementCtx;
}

const addCrossReferenceIssues = ({
  spec,
  context,
}: IAddCrossReferenceIssuesProps): void => {
  const inputs = spec.inputs ?? [];
  const values = spec.values ?? {};
  const inputIds = inputs.map((input) => input.id);
  const valueIds = Object.keys(values);
  const allIds = [...inputIds, ...valueIds];

  if (new Set(allIds).size !== allIds.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "Every input and value id must be unique — the same id is defined twice",
      path: ["inputs"],
    });
  }

  inputs.forEach((input, inputIndex) =>
    checkInput({ input, path: ["inputs", inputIndex], context }),
  );

  const ordered = orderUiValues({ values, inputIds: new Set(inputIds) });

  // NOTE: The error already names its field (`values.total: …`), so it goes
  // out without a path — `formatIssues` would otherwise print "values: " twice.
  if (!ordered.success) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: ordered.error,
      path: [],
    });
  }

  const knownIds = new Set(allIds);

  spec.blocks.forEach((block, blockIndex) =>
    checkBlock({ block, knownIds, path: ["blocks", blockIndex], context }),
  );
};

// NOTE: The input is `unknown`, not `IUiSpec`: the numbers-only fields accept a
// numeral written as text, so what goes in is looser than what comes out.
export const uiSpecSchema: z.ZodType<IUiSpec, unknown> = z
  .object({
    title: titleSchema.optional(),
    inputs: z
      .array(inputSchema)
      .max(MAX_UI_INPUTS, {
        message: `An interface has at most ${MAX_UI_INPUTS} inputs — keep the ones the answer turns on`,
      })
      .optional(),
    values: z
      // NOTE: NSAI-5511 — zod 4 wraps a refused record key in a generic
      // "Invalid key in record" issue; surfacing the key's own message keeps
      // the reason the reader is shown ("This id is reserved ...").
      .record(idSchema, expressionSchema, {
        error: (issue) =>
          issue.code === "invalid_key"
            ? (issue.issues[0]?.message ?? null)
            : null,
      })
      .refine((values) => Object.keys(values).length <= MAX_UI_VALUES, {
        message: `An interface derives at most ${MAX_UI_VALUES} values`,
      })
      .optional(),
    blocks: z
      .array(blockSchema)
      .min(1, { message: "An interface needs at least one block" })
      .max(MAX_UI_BLOCKS, {
        message: `An interface has at most ${MAX_UI_BLOCKS} blocks — a longer one belongs in an html page`,
      }),
  })
  .strict()
  .superRefine((spec, context) => addCrossReferenceIssues({ spec, context }));

const formatIssues = (error: z.ZodError): string =>
  error.issues
    .map((issue) => {
      const path = issue.path.join(".");
      return path.length > 0 ? `${path}: ${issue.message}` : issue.message;
    })
    .join("; ");

export const parseUiSpec = (input: unknown): TParseUiResult => {
  const result = uiSpecSchema.safeParse(input);

  if (result.success) {
    return { success: true, data: result.data };
  }

  // NOTE: NSAI-7841 — the second try: a comparison in the model's own layout,
  // rewritten into the contract's. Drawn only when the rewrite passes; the
  // reason shown otherwise is the one for what the model actually wrote.
  const reshaped = uiSpecSchema.safeParse(reshapeUiComparisons(input));

  if (reshaped.success) {
    return { success: true, data: reshaped.data, isRepaired: true };
  }

  return {
    success: false,
    error: formatIssues(result.error),
    failure: EUiParseFailure.INVALID_SPEC,
  };
};

// NOTE: NSAI-7852 — the second try, once the source as written is not JSON.
// A repaired block is drawn only when it passes the whole contract; otherwise
// the reason stays "not valid JSON", because the source the user is shown is
// the original, not the repaired text.
const parseRepairedUiBlockSource = ({
  rawJson,
  canClose,
}: IRepairUiJsonProps): TParseUiResult => {
  const repairedJson = repairUiJson({ rawJson, canClose });

  if (repairedJson === null) {
    return UI_INVALID_JSON_RESULT;
  }

  try {
    const result = parseUiSpec(JSON.parse(repairedJson));

    return result.success
      ? { ...result, isRepaired: true }
      : UI_INVALID_JSON_RESULT;
  } catch {
    return UI_INVALID_JSON_RESULT;
  }
};

// NOTE: One fenced ```ui block's source, turned into a spec or into the reason
// it cannot be drawn. While the block streams the JSON is truncated, so the
// parse throws — caught here once rather than in every renderer. The repair
// here only removes characters, so a truncated block stays unparseable: use
// this while the block may still be streaming.
export const parseUiBlockSource = (rawJson: string): TParseUiResult => {
  try {
    const parsed: unknown = JSON.parse(rawJson);

    return parseUiSpec(parsed);
  } catch {
    return parseRepairedUiBlockSource({ rawJson });
  }
};

// NOTE: NSAI-7852 — a block that has finished streaming. Removing a stray
// bracket is tried first, because it is the smaller change; only when that
// does not draw the block are missing closing brackets added.
export const parseSettledUiBlockSource = (rawJson: string): TParseUiResult => {
  const result = parseUiBlockSource(rawJson);

  if (result.success || result.failure !== EUiParseFailure.INVALID_JSON) {
    return result;
  }

  return parseRepairedUiBlockSource({ rawJson, canClose: true });
};
